import { Button, Checkbox, Text } from "@chakra-ui/react";
import { FormRow, Select, TextField } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  BusinessFormEnum,
  useCompanyDataAutocompleteQuery,
  useCompanyDataByRegistrationNoLazyQuery,
  useModalContractorDependenciesQuery,
} from "@src/__generated__/graphql";
import { AutocompleteField } from "@src/components/ui-kit/AutocompleteField";
import { Icon } from "@src/components/ui-kit/Icon";
import { appStore } from "@src/stores/AppStore";
import {
  fieldToIBANInputProps,
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { type FC, Fragment } from "react";
import type { Store } from "./store";

type Props = {
  store: Store;
};

export const ModalContractorForm: FC<Props> = observer(
  function ModalContractorForm({ store }) {
    const { workspaceStore } = useStore();
    const { form } = store;

    const { loading: loadingDependencies } =
      useModalContractorDependenciesQuery({
        onCompleted({ countrySimpleMap, accountingAccountsSimpleMap }) {
          if (countrySimpleMap) {
            form.setCountryOptions(countrySimpleMap);
          }
          if (accountingAccountsSimpleMap) {
            form.setAccountingAccountOptions(accountingAccountsSimpleMap);
          }
        },
      });

    const { loading: autocompleteLegalNameLoading } =
      useCompanyDataAutocompleteQuery({
        skip: form.legal_name.value.trim().length < 3,
        variables: {
          input: form.legal_name.value.trim(),
          country_id:
            form.country_id.value !== ""
              ? form.country_id.value
              : appStore.workspaceStore.settings?.country.id!,
        },
        onCompleted({ getAutocompleteForCompanyData }) {
          form.setAutoCompleteNameOptions(
            getAutocompleteForCompanyData.results,
          );
        },
      });

    const { loading: autocompleteRegistrationNoLoading } =
      useCompanyDataAutocompleteQuery({
        skip: form.registration_no.value.trim().length < 6,
        variables: {
          input: form.registration_no.value.trim(),
          country_id:
            form.country_id.value !== ""
              ? form.country_id.value
              : appStore.workspaceStore.settings?.country.id!,
        },
        onCompleted({ getAutocompleteForCompanyData }) {
          form.setAutoCompleteRegistrationNoOptions(
            getAutocompleteForCompanyData.results,
          );
        },
      });

    const [fetchCompanyData] = useCompanyDataByRegistrationNoLazyQuery({
      onCompleted({ getCompanyDataByRegistrationNo }) {
        if (!getCompanyDataByRegistrationNo) return;
        // TODO: Fix if date is going to cause troubles
        // @ts-expect-error
        store.setCompanyData(getCompanyDataByRegistrationNo, {
          bankAccounts: getCompanyDataByRegistrationNo.bankAccounts,
        });
      },
    });

    const handleSelect = (value: string) => {
      fetchCompanyData({
        variables: {
          registration_no: value,
          country_id:
            form.country_id.value !== ""
              ? form.country_id.value
              : appStore.workspaceStore.settings?.country.id!,
        },
      });
    };

    return (
      <Fragment>
        <FormRow title={<Trans>Title</Trans>}>
          <AutocompleteField
            {...fieldToInputProps(form.legal_name)}
            label=""
            onSelect={({ value }) => handleSelect(value)}
            charCount={3}
            loading={autocompleteLegalNameLoading}
            autoCompleteOptions={form.autoCompleteNameOptions.map((option) => ({
              label: (
                <div>
                  <div className="font-bold">{option.name}</div>
                  <div className="text-gray-600 text-xl font-semibold">
                    <Trans>Reg. No: {option.reg_no}</Trans>
                  </div>
                  <div className="text-gray-600 text-xl font-semibold">
                    {option.city}
                  </div>
                </div>
              ),
              value: option.reg_no,
            }))}
          />
        </FormRow>
        <FormRow title={<Trans>Street</Trans>}>
          <TextField type="text" {...fieldToInputProps(form.street)} />
        </FormRow>
        <FormRow title={<Trans>City</Trans>}>
          <TextField type="text" {...fieldToInputProps(form.city)} />
        </FormRow>
        <FormRow title={<Trans>Zip</Trans>}>
          <TextField type="text" {...fieldToInputProps(form.zip)} />
        </FormRow>
        <FormRow title={<Trans>Country</Trans>}>
          <Select
            asPortal
            placeholder={t`Select country`}
            options={store.form.countryOptions}
            isLoading={loadingDependencies}
            {...fieldToInputProps(form.country_id)}
            onChange={(val) => {
              form.country_id.onChange(val);
            }}
          />
        </FormRow>
        <FormRow title={<Trans>Reg. no.</Trans>}>
          <AutocompleteField
            {...fieldToInputProps(form.registration_no)}
            label=""
            onSelect={({ value }) => handleSelect(value)}
            charCount={6}
            error={form.registration_no.error}
            loading={autocompleteRegistrationNoLoading}
            autoCompleteOptions={form.autoCompleteRegistrationNoOptions.map(
              (option) => ({
                label: (
                  <div>
                    <div className="font-bold">{option.name}</div>
                    <div className="text-gray-600 text-xl font-semibold">
                      <Trans>Reg. No: {option.reg_no}</Trans>
                    </div>
                    <div className="text-gray-600 text-xl font-semibold">
                      {option.city}
                    </div>
                  </div>
                ),
                value: option.reg_no,
              }),
            )}
          />
        </FormRow>
        <FormRow title={<Trans>Business form</Trans>}>
          <Select
            asPortal
            placeholder={t`Select business form`}
            {...fieldToSelectProps(
              form.business_form,
              store.form.businessFormOptions,
            )}
          />
        </FormRow>

        <FormRow title={<Trans>TAX ID</Trans>}>
          <TextField
            type="text"
            value={form.tax_no.value}
            error={form.tax_no.error}
            onChange={form.handleTaxNoChange}
          />
        </FormRow>
        <FormRow title={<Trans>Vat payer</Trans>}>
          <Checkbox
            isChecked={form.vat_payer.value}
            onChange={(e) => form.handleVatPayerChange(e.target.checked)}
          />
        </FormRow>
        {!!form.vat_payer.$ && (
          <FormRow title={<Trans>VAT no.</Trans>}>
            <TextField
              type="text"
              value={form.vat_no.value}
              error={form.vat_no.error}
              onChange={form.handleVatNoChange}
            />
          </FormRow>
        )}
        {form.business_form.value === BusinessFormEnum.Person && (
          <FormRow title={<Trans>National ID</Trans>}>
            <TextField type="text" {...fieldToInputProps(form.national_id)} />
          </FormRow>
        )}
        <FormRow title={<Trans>Account classification</Trans>}>
          <Select
            asPortal
            placeholder={t`Select account classification`}
            isLoading={loadingDependencies}
            {...fieldToSelectProps(
              form.default_accounting_account_id,
              store.form.accountingAccountOptions,
            )}
          />
        </FormRow>
        <FormRow title={<Trans>IBAN</Trans>}>
          {form.ibanOptions.length ? (
            <Select
              asPortal
              isClearable
              isCreatable
              placeholder={t`Select IBAN`}
              {...fieldToSelectProps(
                form.iban,
                store.form.ibanOptions,
                (iban) => {
                  const swift = form.bankAccounts.find(
                    (account) => account.iban === iban,
                  )?.swift;
                  form.swift.onChange(swift || "");
                  // reset Error that we set in `store.setCompanyData` function
                  form.iban.reset();
                  return iban;
                },
              )}
            />
          ) : (
            <TextField type="text" {...fieldToIBANInputProps(form.iban)} />
          )}
          <Text mt="1" color="gray.500" fontSize="sm" lineHeight="1">
            <Icon name="info-circle" />
            &nbsp;
            <Trans>To generate a payment order, please set an IBAN</Trans>
          </Text>
        </FormRow>
        {workspaceStore.settings?.enableContractorFullBankAccountData && (
          <>
            <FormRow title={<Trans>Bank account</Trans>}>
              <TextField
                value={form.bank_account.value}
                onChange={form.bank_account.onChange}
                error={form.bank_account.error}
              />
            </FormRow>
            <FormRow title={<Trans>Bank code</Trans>}>
              <TextField
                value={form.bank_account_code.value}
                onChange={form.bank_account_code.onChange}
                error={form.bank_account_code.error}
              />
            </FormRow>
            {store.isCzechContractor && (
              <FormRow>
                <Button
                  isDisabled={!store.canConvertBankAccountToIban}
                  isLoading={store.isConvertingBankAccountToIban}
                  onClick={store.convertBankAccountToIban}
                  size="sm"
                  variant="outline"
                >
                  <Trans>Convert bank account to IBAN</Trans>
                </Button>
              </FormRow>
            )}
          </>
        )}
        <FormRow title={<Trans>SWIFT</Trans>}>
          <TextField
            isDisabled={
              form.usedIbanFromAutocompletedBankAccounts && !!form.swift.value
            }
            type="text"
            {...fieldToInputProps(form.swift)}
          />
        </FormRow>
      </Fragment>
    );
  },
);
